import { MINE_COUNT, WIN_THRESHOLD, type GameSnapshot, type Team } from "@msf/shared";
import { BombIcon, FlagIcon } from "./icons.js";

interface ScoreBarProps {
  snapshot: GameSnapshot;
  you: Team | null;
  onBomb: () => void;
  bombArmed: boolean;
}

function seatOf(snapshot: GameSnapshot, team: Team) {
  return snapshot.players.find((p) => p.team === team);
}

export function ScoreBar({ snapshot, you, onBomb, bombArmed }: ScoreBarProps) {
  const { scores, turn, status } = snapshot;
  const redPct = (scores.red / MINE_COUNT) * 100;
  const bluePct = (scores.blue / MINE_COUNT) * 100;
  const active = status === "active";

  return (
    <div className="clay mx-auto w-full max-w-[30rem] p-3">
      <div className="flex items-stretch gap-2">
        <TeamCard
          team="red"
          name={seatOf(snapshot, "red")?.nickname ?? "Waiting…"}
          score={scores.red}
          isTurn={active && turn === "red"}
          isYou={you === "red"}
          online={seatOf(snapshot, "red")?.connected ?? false}
          isAi={seatOf(snapshot, "red")?.isAi ?? false}
        />
        <div className="flex flex-col items-center justify-center px-1">
          <span className="text-[0.6rem] uppercase tracking-wide opacity-60">mines left</span>
          <span className="text-2xl font-black leading-none">{snapshot.minesRemaining}</span>
          <span className="text-[0.6rem] opacity-60">first to {WIN_THRESHOLD}</span>
        </div>
        <TeamCard
          team="blue"
          name={seatOf(snapshot, "blue")?.nickname ?? "Waiting…"}
          score={scores.blue}
          isTurn={active && turn === "blue"}
          isYou={you === "blue"}
          online={seatOf(snapshot, "blue")?.connected ?? false}
          isAi={seatOf(snapshot, "blue")?.isAi ?? false}
        />
      </div>

      {/* Territory bar */}
      <div className="mt-2 flex h-2.5 overflow-hidden rounded-full" style={{ background: "var(--revealed)" }}>
        <div
          style={{ width: `${redPct}%`, background: "var(--color-red-team, #f2685f)" }}
          className="transition-all"
        />
        <div className="flex-1" />
        <div
          style={{ width: `${bluePct}%`, background: "var(--color-blue-team, #5aa9e6)" }}
          className="transition-all"
        />
      </div>

      {you &&
        active &&
        (() => {
          const has = snapshot.bombsAvailable[you];
          const canBomb = has && turn === you;
          const label = bombArmed ? "Tap a tile to bomb" : has ? "Use bomb" : "Bomb used";
          return (
            <button
              type="button"
              onClick={onBomb}
              disabled={!canBomb}
              className={`clay-btn mt-3 flex w-full items-center gap-2.5 px-3 py-2 text-left disabled:opacity-40 ${
                canBomb ? "clay-accent" : ""
              } ${bombArmed ? "ring-4 ring-amber-300" : ""}`}
            >
              {/* Light chip keeps the dark clay bomb legible on any button colour. */}
              <span
                className="grid h-8 w-8 shrink-0 place-items-center rounded-full"
                style={{ background: "rgba(255,255,255,0.85)" }}
              >
                <BombIcon className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-bold">{label}</span>
                <span className="block truncate text-[0.65rem] font-normal opacity-70">
                  Reveals 9 tiles instead of 1
                </span>
              </span>
              {has && (
                <span
                  className="shrink-0 rounded-full px-2 py-0.5 text-[0.65rem] font-bold"
                  style={{ background: "rgba(255,255,255,0.6)" }}
                >
                  1 use
                </span>
              )}
            </button>
          );
        })()}
    </div>
  );
}

function TeamCard({
  team,
  name,
  score,
  isTurn,
  isYou,
  online,
  isAi,
}: {
  team: Team;
  name: string;
  score: number;
  isTurn: boolean;
  isYou: boolean;
  online: boolean;
  isAi: boolean;
}) {
  const accent = team === "red" ? "#f2685f" : "#5aa9e6";
  return (
    <div
      className={`flex flex-1 items-center gap-2 rounded-2xl px-2 py-1.5 transition ${isTurn ? "scale-[1.02]" : "opacity-90"}`}
      style={{
        background: isTurn ? `${accent}22` : "transparent",
        boxShadow: isTurn ? `inset 0 0 0 2px ${accent}55` : "none",
      }}
    >
      <FlagIcon team={team} className="h-7 w-7 shrink-0" />
      <div className="min-w-0">
        <div className="flex items-center gap-1">
          <span className="truncate text-sm font-bold">{name}</span>
          {isYou && <span className="rounded bg-black/10 px-1 text-[0.55rem] font-bold uppercase">you</span>}
          {isAi && <span className="rounded bg-black/10 px-1 text-[0.55rem] font-bold uppercase">ai</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-lg font-black leading-none" style={{ color: accent }}>
            {score}
          </span>
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: online ? "#54c17a" : "#c0b39a" }}
            title={online ? "online" : "offline"}
          />
        </div>
      </div>
    </div>
  );
}
